import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminStats } from "@/hooks/useAdminStats";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DealMeetingsSection } from "@/components/meetings/DealMeetingsSection";
import { AdminContactRequests } from "@/components/dashboard/ContactRequestsTracker";
import { useContactRequestNotifications } from "@/hooks/useContactRequestNotifications";
import { StaffScheduler } from "@/components/admin/StaffScheduler";
import {
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Briefcase,
  Clock,
  Target,
  Activity,
  Handshake,
  UserCog,
  Sparkles,
  Rocket,
  Swords,
  Video,
  CalendarClock,
} from "lucide-react";

const translations = {
  en: {
    overview: "Overview",
    welcome: "Welcome to the Super Admin Dashboard",
    liveData: "Live Data",
    changeStatus: "Change Status",
    superAdmin: "Super Admin",
    roles: {
      admin: "Admin",
      staff: "Staff",
      support: "Tech Support",
      seller: "Seller",
      broker: "Broker",
      visitor: "Visitor",
      basic: "Basic Plan",
      essential: "Essential Plan",
      pro: "Pro Plan",
      lender: "Lender",
      consultant: "Consultant",
      businessLawyer: "Business Lawyer",
      immigrationLawyer: "Immigration Lawyer",
      accountant: "Accountant",
      taxAdvisor: "Tax Advisor",
      notary: "Notary",
    },
  },
  fr: {
    overview: "Vue d'ensemble",
    welcome: "Bienvenue sur le tableau de bord Super Admin",
    liveData: "Données en direct",
    changeStatus: "Changer de statut",
    superAdmin: "Super Admin",
    roles: {
      admin: "Admin",
      staff: "Staff",
      support: "Support technique",
      seller: "Vendeur",
      broker: "Courtier",
      visitor: "Visiteur",
      basic: "Forfait Basic",
      essential: "Forfait Essentiel",
      pro: "Forfait Pro",
      lender: "Prêteur",
      consultant: "Consultant",
      businessLawyer: "Avocat en droit des affaires",
      immigrationLawyer: "Avocat en immigration",
      accountant: "Comptable",
      taxAdvisor: "Conseiller fiscal",
      notary: "Notaire",
    },
  },
};

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  trendValue,
  loading,
}: {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  trend?: "up" | "down" | "neutral";
  trendValue?: string;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24 bg-gray-700" />
          <Skeleton className="h-8 w-8 rounded bg-gray-700" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 bg-gray-700 mb-2" />
          <Skeleton className="h-3 w-32 bg-gray-700" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-300">{title}</CardTitle>
        <div className="p-2 rounded-lg bg-amber-500/20">
          <Icon className="h-5 w-5 text-amber-500" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="flex items-center gap-2 mt-1">
          {trend && trendValue && (
            <Badge
              variant="outline"
              className={
                trend === "up"
                  ? "text-green-400 border-green-400/50"
                  : trend === "down"
                  ? "text-red-400 border-red-400/50"
                  : "text-gray-400 border-gray-600"
              }
            >
              {trend === "up" ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : trend === "down" ? (
                <TrendingDown className="h-3 w-3 mr-1" />
              ) : null}
              {trendValue}
            </Badge>
          )}
          {description && <p className="text-xs text-gray-400">{description}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { stats, loading } = useAdminStats();
  const navigate = useNavigate();
  const [currentRole, setCurrentRole] = useState("admin");
  
  const t = translations[language as keyof typeof translations] || translations.en;

  // Real-time notifications for new contact requests
  useContactRequestNotifications({ role: 'staff' });

  const roleRoutes: Record<string, string> = {
    admin: "/admin/overview",
    staff: "/staff-dashboard",
    support: "/admin/overview",
    seller: "/seller/dashboard",
    broker: "/dashboard/broker",
    visitor: "/buyer/dashboard",
    basic: "/buyer/dashboard",
    essential: "/buyer/dashboard",
    pro: "/buyer/dashboard",
    lender: "/dashboard/lender",
    consultant: "/dashboard/consultant",
    businessLawyer: "/internal-partners/business-law",
    immigrationLawyer: "/internal-partners/immigration-law",
    accountant: "/internal-partners/cpa",
    taxAdvisor: "/internal-partners/tax-advisor",
    notary: "/internal-partners/notary",
  };

  const roleOptions = [
    { key: "admin", label: t.roles.admin },
    { key: "staff", label: t.roles.staff },
    { key: "support", label: t.roles.support },
    { key: "seller", label: t.roles.seller },
    { key: "broker", label: t.roles.broker },
    { key: "visitor", label: t.roles.visitor },
    { key: "basic", label: t.roles.basic },
    { key: "essential", label: t.roles.essential },
    { key: "pro", label: t.roles.pro },
    { key: "lender", label: t.roles.lender },
    { key: "consultant", label: t.roles.consultant },
    { key: "businessLawyer", label: t.roles.businessLawyer },
    { key: "immigrationLawyer", label: t.roles.immigrationLawyer },
    { key: "accountant", label: t.roles.accountant },
    { key: "taxAdvisor", label: t.roles.taxAdvisor },
    { key: "notary", label: t.roles.notary },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">{t.overview}</h1>
          <p className="text-gray-400 mt-1">{t.welcome}</p>
        </div>
        <Badge variant="outline" className="text-amber-500 border-amber-500/50 w-fit">
          <Activity className="h-3 w-3 mr-1" />
          {t.liveData}
        </Badge>
      </div>

      {/* User Info & Role Buttons */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">{user?.displayName || "Veronique Amyot"}</CardTitle>
              <CardDescription className="text-gray-400">{t.superAdmin}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-300 mb-3">{t.changeStatus}</p>
          <div className="flex flex-wrap gap-2">
            {roleOptions.map((role) => (
              <Button
                key={role.key}
                variant={currentRole === role.key ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setCurrentRole(role.key);
                  navigate(roleRoutes[role.key] || "/admin/overview");
                }}
                className={
                  currentRole === role.key
                    ? "bg-amber-500 text-gray-900 hover:bg-amber-600"
                    : "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                }
              >
                {role.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Deal Flow Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-amber-500" />
          Deal Flow
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Active Deals"
            value={stats.activeDeals}
            description="Currently in pipeline"
            icon={FileText}
            loading={loading}
          />
          <StatCard
            title="Sold Deals"
            value={stats.soldDeals}
            description="Successfully closed"
            icon={Target}
            trend="up"
            trendValue="+12%"
            loading={loading}
          />
          <StatCard
            title="Submissions (30d)"
            value={stats.submissions30d}
            description="New deal submissions"
            icon={BarChart3}
            loading={loading}
          />
          <StatCard
            title="Conversion Rate"
            value={`${stats.submissionToListedRate.toFixed(1)}%`}
            description="Submission to Listed"
            icon={TrendingUp}
            loading={loading}
          />
        </div>
      </section>

      {/* Activity Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-amber-500" />
          Activity (Last 30 Days)
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="NDAs Signed"
            value={stats.ndas30d}
            icon={FileText}
            loading={loading}
          />
          <StatCard
            title="Offers Made"
            value={stats.offers30d}
            icon={DollarSign}
            loading={loading}
          />
          <StatCard
            title="Favorites"
            value={stats.favorites30d}
            icon={Target}
            loading={loading}
          />
          <StatCard
            title="NDA to Offer Rate"
            value={`${stats.ndaToOfferRate.toFixed(1)}%`}
            icon={TrendingUp}
            loading={loading}
          />
        </div>
      </section>

      {/* Users Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-amber-500" />
          Users & Clients
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={Users}
            loading={loading}
          />
          <StatCard
            title="Sellers"
            value={stats.totalSellers}
            icon={Users}
            loading={loading}
          />
          <StatCard
            title="Brokers"
            value={stats.totalBrokers}
            icon={Users}
            loading={loading}
          />
          <StatCard
            title="Buyers"
            value={stats.totalBuyers}
            icon={Users}
            loading={loading}
          />
          <StatCard
            title="Consultants"
            value={stats.totalConsultants}
            icon={Users}
            loading={loading}
          />
          <StatCard
            title="Lenders"
            value={stats.totalLenders}
            icon={Users}
            loading={loading}
          />
        </div>
      </section>

      {/* Staff & Partners Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <UserCog className="h-5 w-5 text-amber-500" />
          Staff & Partners
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            title="Deals In Progress"
            value={stats.staffDealsInProgress}
            description="Staff workload"
            icon={Clock}
            loading={loading}
          />
          <StatCard
            title="CIM Ready for Review"
            value={stats.cimReadyForReview}
            description="Pending internal review"
            icon={FileText}
            loading={loading}
          />
          <StatCard
            title="Partner Tasks Open"
            value={stats.partnerAssignmentsOpen}
            description="Awaiting completion"
            icon={Handshake}
            loading={loading}
          />
        </div>
      </section>

      {/* AI Documents Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          AI Documents
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="CIMs Generated"
            value={stats.totalCimsGenerated}
            description="Total CIM documents"
            icon={FileText}
            loading={loading}
          />
          <StatCard
            title="Market Studies"
            value={stats.totalMarketStudies}
            description="Generated studies"
            icon={BarChart3}
            loading={loading}
          />
          <StatCard
            title="Strategic Plans"
            value={stats.totalStrategicPlans}
            description="Growth plans created"
            icon={Rocket}
            loading={loading}
          />
          <StatCard
            title="Competition Reports"
            value={stats.totalCompetitionReports}
            description="SWOT analyses"
            icon={Swords}
            loading={loading}
          />
          <StatCard
            title="Total AI Docs"
            value={stats.aiDocsGenerated30d}
            description="All AI documents"
            icon={Sparkles}
            loading={loading}
          />
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <NavLink to="/admin/ai/market-study">
            <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/20">
              <BarChart3 className="h-4 w-4 mr-2" />
              Generate Market Study
            </Button>
          </NavLink>
          <NavLink to="/admin/ai/strategic-plan">
            <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/20">
              <Rocket className="h-4 w-4 mr-2" />
              Generate Strategic Plan
            </Button>
          </NavLink>
          <NavLink to="/admin/ai/competition-report">
            <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/20">
              <Swords className="h-4 w-4 mr-2" />
              Generate Competition Report
            </Button>
          </NavLink>
        </div>
      </section>

      {/* Contact Requests Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-amber-500" />
          {language === 'fr' ? 'Demandes de Contact' : 'Contact Requests'}
        </h2>
        <AdminContactRequests language={language} />
      </section>

      {/* Staff Scheduler Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-amber-500" />
          {language === 'fr' ? 'Planification du Staff' : 'Staff Scheduling'}
        </h2>
        <StaffScheduler />
      </section>

      {/* Meetings Section */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Video className="h-5 w-5 text-amber-500" />
          Recent Meetings
        </h2>
        <DealMeetingsSection />
      </section>
    </div>
  );
}
